import { useEffect, useState } from 'react';

import { Box, Form, Toast, useToast } from '@zillow/constellation-10';

import { convertAvailabilityDatesToSetAvailabilityLiteDto } from '@zg-rentals/touring';
import { DatarouterError } from '@zg-rentals/web-base';

import { useTourSchedulingContext } from '../../../../contexts/TourSchedulingContext';

import TourSchedulingAvailabilityBody from './components/TourSchedulingAvailabilityBody';

import { wizardFlexContainerStyles } from '../../../../styles/wizard';

import SpinnerContainer from '../../../SpinnerContainer';
import PageWizard from '../../../Wizard/PageWizard';
import { useWizardNavigation } from '../../WizardNavigationProvider';

import type { TimeZoneIds } from '@zg-rentals/touring';
import type { TourDuration } from '@zg-rentals/ts-z-calendar-client';
import type { WizardBodyTemplateProps } from '../../../WizardStepController';

const TourSchedulingAvailability = ({
  onGoToNextStep,
  onGoToPrevStep,
  onExitWizard,
  wizardTitle,
  progressStepper,
  totalSteps,
  currentStepIndex,
  ...rest
}: WizardBodyTemplateProps) => {
  const { enqueueToast } = useToast();
  const { availabilityDates, setAvailabilityDates, selectedTimeZone, selectedTourDuration, tourCalendarApiHook } =
    useTourSchedulingContext();

  const [isNavigating, setIsNavigating] = useState(false);

  const isNextDisabled = isNavigating;

  const { reportError, clearError } = useWizardNavigation();
  useEffect(() => {
    if (isNextDisabled) {
      reportError();
      return () => clearError();
    }
  }, [isNextDisabled, reportError, clearError]);

  const onNextClick = async () => {
    if (isNavigating) return;
    setIsNavigating(true);

    try {
      const setAvailabilityLiteDto = convertAvailabilityDatesToSetAvailabilityLiteDto(availabilityDates);
      await tourCalendarApiHook.setAvailabilitiesLite(setAvailabilityLiteDto);
      onGoToNextStep?.();
    } catch (error) {
      const errorMessage = DatarouterError.getDisplayMessage(error);
      enqueueToast(<Toast tone="critical">{errorMessage}</Toast>);
    } finally {
      setIsNavigating(false);
    }
  };

  return (
    <PageWizard
      {...rest}
      title=""
      wizardTitle={wizardTitle}
      progressStepper={progressStepper}
      totalSteps={totalSteps}
      currentStepIndex={currentStepIndex}
      onNextClick={onNextClick}
      onBackClick={onGoToPrevStep || undefined}
      onExitTriggered={onExitWizard}
      isNextDisabled={isNextDisabled}
    >
      <div data-testid="TourSchedulingAvailability">
        {tourCalendarApiHook.isGetTourConfigAndAvailabilityLoading ? (
          <SpinnerContainer />
        ) : (
          <Box css={wizardFlexContainerStyles}>
            <Form asChild onSubmit={(e) => e.preventDefault()}>
              <TourSchedulingAvailabilityBody
                availabilityDates={availabilityDates}
                onAvailabilityDatesChange={setAvailabilityDates}
                timeZone={selectedTimeZone as TimeZoneIds}
                tourDuration={selectedTourDuration as TourDuration}
              />
            </Form>
          </Box>
        )}
      </div>
    </PageWizard>
  );
};

export default TourSchedulingAvailability;
